import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/sharp-solid-svg-icons';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Button, { ButtonVariant } from '@/components/button';
import Checkbox from '@/components/checkbox';
import FileDropZone, { FilePreview } from '@/components/fileDropZone';
import TextInput from '@/components/input/textInput';
import BaseModal from '@/components/modal/baseModal';
import { BasicModalFooterWrapper } from '@/components/modal/basicModalFooter';
import ModalHeading from '@/components/modal/modalHeading';
import Toast, { ToastOrientation, ToastVariant } from '@/components/toast';
import Typography from '@/components/typography';
import { GLOBAL_TOOLTIP, THREE_MB } from '@/constants/constants';
import { generateAerialReport } from '@/endpoints/itasca/reports/generateAerialReport';
import { useModalContext } from '@/providers/ModalProvider';
import { useToastContext } from '@/providers/ToastProvider';
import { fileValidator } from '@/utils/fileValidator';

import { Orientation } from './aerialMapToast';

type FormData = {
  image: File;
  name: string;
  orientation: Orientation;
  logo: File | null;
  contact_name: string;
  contact_email: string;
};

type Props = {
  image: File;
  orientation: Orientation;
  activeFileId: number | null;
};

const acceptedFileTypes = ['jpeg', 'jpg', 'png', 'webp'];
const acceptedFileMimeTypes = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
};

const schema = yup.object({
  contact_email: yup
    .string()
    .email('Must be a valid email')
    .nullable()
    .max(50, 'Must be less than 50 characters.'),
  contact_name: yup
    .string()
    .nullable()
    .max(50, 'Must be less than 50 characters.'),
  image: yup.mixed().required('This field is required'),
  logo: yup.mixed().nullable(),
  name: yup
    .string()
    .required('This field is required')
    .max(50, 'Must be less than 50 characters.'),
  orientation: yup.string().required('This field is required'),
});

const defaultValues: FormData = {
  contact_email: '',
  contact_name: '',
  image: new File([], ''), // Empty file
  logo: null,
  name: '',
  orientation: 'landscape' as Orientation,
};

const formId = 'aerial-map-modal-form';

const AerialMapModal: FC<Props> = ({ image, orientation, activeFileId }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [includeLogo, setIncludeLogo] = useState<boolean>(false);
  const [includeContactInfo, setIncludeContactInfo] = useState<boolean>(false);
  const [logo, setLogo] = useState<FilePreview | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { setToastContent, clearToastContent } = useToastContext();
  const { clearModalContent } = useModalContext();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length < 2) {
        const files = acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        );
        if (files[0]) {
          setLogo(files[0]);
        }
      }
    },
    [setLogo]
  );

  const showSuccessToast = useCallback(() => {
    clearModalContent();
    setToastContent(
      <Toast
        canClose
        orientation={ToastOrientation.INLINE}
        subtitle="Please check your email."
        title="Aerial Report Generated"
        variant={ToastVariant.SUCCESS}
        onClose={clearToastContent}
      />
    );
  }, [clearModalContent, clearToastContent, setToastContent]);

  const handleAerialReport = useCallback(
    async (data: FormData) => {
      setIsLoading(true);

      const formData = new FormData();
      formData.append('contact_email', data.contact_email);
      formData.append('contact_name', data.contact_name);
      formData.append('image', data.image);
      formData.append('logo', data.logo ?? '');
      formData.append('name', data.name);
      formData.append('orientation', data.orientation);
      formData.append('saved_selection__id', activeFileId?.toString() ?? '');
      formData.append('display_date', dayjs().format('YYYY-MM-DD'));

      const { res, err } = await generateAerialReport(formData);

      if (err || !res || !(res.status >= 200 && res.status < 300)) {
        setErrorMessage(err?.message || 'An error has occurred');
      }

      if (res && res.status === 201) {
        showSuccessToast();
      }

      setIsLoading(false);
    },
    [activeFileId, showSuccessToast]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } =
    useDropzone({
      accept: acceptedFileMimeTypes,
      maxFiles: 1,
      multiple: false,
      onDrop,
      validator: fileValidator(acceptedFileTypes, undefined, THREE_MB),
    });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      ...defaultValues,
      image,
      orientation,
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (logo) {
      setValue('logo', logo);
    }
  }, [logo, setValue]);

  const tooltipContent = useMemo(() => {
    return !isValid ? 'Missing or invalid field(s).' : undefined;
  }, [isValid]);

  return (
    <BaseModal
      Content={
        <div>
          <form id={formId} />
          <TextInput
            required
            control={control}
            label="Report Name"
            name="name"
            placeholder="Name your report"
          />

          <Checkbox
            checked={includeLogo}
            label="Include logo"
            onChange={() => setIncludeLogo(!includeLogo)}
          />

          {includeLogo && (
            <>
              <FileDropZone
                acceptedFiles={acceptedFileTypes}
                activeImg={logo}
                className="h-[165px]"
                clearActiveImg={(e) => {
                  e.preventDefault();
                  setLogo(null);
                  setValue('logo', null);
                }}
                getInputProps={getInputProps}
                getRootProps={getRootProps}
                isDragActive={isDragActive}
              />
              {fileRejections.slice(0, 1).map(({ errors: err }) => (
                <>
                  {err.map((e) => (
                    <Typography
                      key={e.code}
                      className="mt-[4px] text-red-utility"
                      variant="caption"
                    >
                      {e.message}
                    </Typography>
                  ))}
                </>
              ))}
            </>
          )}

          <Checkbox
            checked={includeContactInfo}
            className="py-2"
            label="Add contact"
            onChange={() => setIncludeContactInfo(!includeContactInfo)}
          />

          {includeContactInfo && (
            <>
              <TextInput
                control={control}
                label="Name"
                name="contact_name"
                placeholder="Enter Contact Name"
              />
              <TextInput
                control={control}
                label="Email"
                name="contact_email"
                placeholder="Enter Contact Email"
              />
            </>
          )}

          {errorMessage && (
            <Typography className="mt-4 text-red-utility" variant="caption">
              {errorMessage}
            </Typography>
          )}
        </div>
      }
      Footer={
        <BasicModalFooterWrapper>
          <Button variant={ButtonVariant.GHOST} onClick={clearModalContent}>
            Cancel
          </Button>
          <div
            data-tooltip-content={tooltipContent}
            data-tooltip-delay-show={300}
            data-tooltip-id={GLOBAL_TOOLTIP}
          >
            <Button
              disabled={!isValid}
              isLoading={isLoading}
              variant={ButtonVariant.PRIMARY}
              onClick={handleSubmit((data) => {
                handleAerialReport(data);
              })}
            >
              Generate Report
            </Button>
          </div>
        </BasicModalFooterWrapper>
      }
      Header={
        <ModalHeading
          iconAndHeading
          icon={
            <FontAwesomeIcon
              className="pr-2"
              color="#449C69"
              icon={faCircleCheck}
              size="xs"
            />
          }
          optionalHeading="Screenshot added"
          title="Generate Aerial Map"
          onXClick={clearModalContent}
        />
      }
      className="min-h-[290px] w-[400px]"
    />
  );
};

export default memo(AerialMapModal);
